import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import {
  FormSection,
  MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
  FilterConfig,
  MasterDataTable,
  TableColumn,
} from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { buildCsv } from '../lib/csv';
import { downloadText } from '../lib/download';

interface Service {
  _id: string;
  name: string;
  code?: string;
  company?: string | { _id: string; name: string };
  address?: string;
  type?: 'DOMESTIC' | 'INTERNATIONAL';
  serviceType?: string | { _id: string; name: string };
  mode?: string;
  minActualWeightPerPcs?: number;
  minVolumeWeightPerPcs?: number;
  maxActualWeightPerPcs?: number;
  maxVolumeWeightPerPcs?: number;
  minActualWeightPerAwb?: number;
  minVolumeWeightPerAwb?: number;
  maxActualWeightPerAwb?: number;
  maxVolumeWeightPerAwb?: number;
  minChargeableWeightPerAwb?: number;
  maxChargeableWeightPerAwb?: number;
  maxPcsPerAwb?: number;
  maxShipmentValue?: number;
  checkMaxShipmentValue?: boolean;
  maxDimsPerLineItem?: number;
  hub?: string | { _id: string; name: string };
  gstNo?: string;
  serviceCode?: string;
  licenseLocation?: string | { _id: string; name: string };
  invoiceHead?: 'TAX_INVOICE' | 'WEEKLY_SETTLEMENT';
  authorityLetterHead?: string;
  trackingUrl?: string;
  trackingWebsite?: string;
  accountNumbers?: string;
  receiverServiceCode?: string;
  currency?: string;
  csPerson?: string;
  billingPerson?: string;
  notes?: string;
  serviceLogo?: string;
  cutOffTime?: string;
  supportedVendor?: string;
  returnAddress?: string;
  status: 'Active' | 'Inactive';
  createdAt: string;
  updatedAt: string;
}

const typeOptions = [
  { value: 'DOMESTIC', label: 'Domestic' },
  { value: 'INTERNATIONAL', label: 'International' },
];

const invoiceHeadOptions = [
  { value: 'TAX_INVOICE', label: 'Tax Invoice' },
  { value: 'WEEKLY_SETTLEMENT', label: 'Weekly Settlement' },
];

const ServiceManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>({});

  // Fetch services
  const { data: servicesResponse, isLoading, refetch } = useQuery({
    queryKey: ['services', currentPage, pageSize, searchTerm, currentFilters, sortConfig],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());
      if (searchTerm) params.append('search', searchTerm);
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') params.append(key, value);
      });
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }
      const response = await axios.get(`/api/master-data/service?${params.toString()}`);
      const result = response.data.data || {};
      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  const services = servicesResponse?.data || [];
  const pagination = servicesResponse?.pagination;

  // Fetch companies for dropdown
  const { data: companies = [] } = useQuery({
    queryKey: ['companies-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/company');
      return response.data?.data?.data || [];
    },
  });

  // Fetch service types for dropdown
  const { data: serviceTypes = [] } = useQuery({
    queryKey: ['serviceTypes-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/serviceType');
      return response.data?.data?.data || [];
    },
  });

  // Fetch hubs for dropdown
  const { data: hubs = [] } = useQuery({
    queryKey: ['hubs-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/hub');
      return response.data?.data?.data || [];
    },
  });

  // Fetch license locations for dropdown
  const { data: licenseLocations = [] } = useQuery({
    queryKey: ['licenseLocations-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/licenseLocation');
      return response.data?.data?.data || [];
    },
  });

  // Create mutation
  const createServiceMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/service', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      toast({ title: 'Success', description: 'Service created successfully' });
      setIsFormOpen(false);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message || 'Failed to create service';
      setServerErrors(error.response?.data?.error?.details || {});
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
    },
  });

  // Update mutation
  const updateServiceMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(`/api/master-data/service/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      toast({ title: 'Success', description: 'Service updated successfully' });
      setIsFormOpen(false);
      setEditingService(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message || 'Failed to update service';
      setServerErrors(error.response?.data?.error?.details || {});
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteServiceMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`/api/master-data/service/${id}?soft=false`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      toast({ title: 'Success', description: 'Service deleted successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to delete service',
        variant: 'destructive',
      });
    },
  });

  // Helper to extract ID from object
  const extractId = (value: any) => {
    if (typeof value === 'object' && value !== null && value._id) return value._id;
    return value;
  };

  // Transform service for editing
  const transformServiceForEdit = (service: Service) => ({
    ...service,
    company: extractId(service.company),
    serviceType: extractId(service.serviceType),
    hub: extractId(service.hub),
    licenseLocation: extractId(service.licenseLocation),
  });

  // Table columns
  const columns: TableColumn<Service>[] = [
    { key: 'code', label: 'Code', sortable: true, render: (_, s) => <span className="font-mono">{s.code || '-'}</span> },
    { key: 'name', label: 'Name', sortable: true, render: (_, s) => <span className="font-medium">{s.name}</span> },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      render: (_, s) => (
        <Badge variant="outline" className={s.type === 'DOMESTIC' ? 'border-blue-500 text-blue-600' : 'border-purple-500 text-purple-600'}>
          {s.type}
        </Badge>
      ),
    },
    {
      key: 'company',
      label: 'Company',
      render: (_, s) => {
        const company = s.company;
        if (typeof company === 'object' && company?.name) return <span>{company.name}</span>;
        return <span>{(company as string) || '-'}</span>;
      },
    },
    {
      key: 'serviceType',
      label: 'Service Type',
      render: (_, s) => {
        const st = s.serviceType;
        if (typeof st === 'object' && st?.name) return <span>{st.name}</span>;
        return <span>{(st as string) || '-'}</span>;
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_, s) => (
        <Badge variant={s.status === 'Active' ? 'default' : 'secondary'} className={s.status === 'Active' ? 'bg-green-500' : ''}>
          {s.status}
        </Badge>
      ),
    },
  ];

  // Filters
  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [{ value: 'all', label: 'All' }, { value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }],
    },
    {
      key: 'type',
      label: 'Type',
      type: 'select',
      options: [{ value: 'all', label: 'All' }, ...typeOptions],
    },
  ];

  // Form sections
  const formSections: FormSection[] = [
    {
      title: 'Basic Information',
      description: 'Service name, code, and type',
      fields: [
        { name: 'name', label: 'Service Name', type: 'text', required: true, placeholder: 'Enter service name', validation: z.string().min(1, 'Name is required').max(200) },
        { name: 'code', label: 'Service Code', type: 'text', required: false, placeholder: 'Auto-generated if empty', description: 'Leave empty for auto-generation' },
        { name: 'company', label: 'Company', type: 'select', required: true, options: companies.map((c: any) => ({ value: c._id, label: c.name })), placeholder: 'Select company' },
        { name: 'type', label: 'Type', type: 'select', required: true, options: typeOptions, placeholder: 'Select type (Domestic/International)' },
        { name: 'serviceType', label: 'Service Type', type: 'select', required: true, options: serviceTypes.map((st: any) => ({ value: st._id, label: st.name })), placeholder: 'Select service type' },
        { name: 'status', label: 'Status', type: 'radio', required: true, options: [{ value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }] },
        { name: 'address', label: 'Address', type: 'textarea', required: false, placeholder: 'Physical address' },
        { name: 'mode', label: 'Mode', type: 'text', required: false, placeholder: 'Enter mode' },
      ],
    },
    {
      title: 'Weight Limits - Per Piece',
      description: 'Minimum and maximum weight limits per piece',
      fields: [
        { name: 'minActualWeightPerPcs', label: 'Min Actual Weight per Pcs', type: 'number', required: false, placeholder: '0' },
        { name: 'minVolumeWeightPerPcs', label: 'Min Volume Weight per Pcs', type: 'number', required: false, placeholder: '0' },
        { name: 'maxActualWeightPerPcs', label: 'Max Actual Weight per Pcs', type: 'number', required: false, placeholder: '0' },
        { name: 'maxVolumeWeightPerPcs', label: 'Max Volume Weight per Pcs', type: 'number', required: false, placeholder: '0' },
      ],
    },
    {
      title: 'Weight Limits - Per AWB',
      description: 'Minimum and maximum weight limits per AWB',
      fields: [
        { name: 'minActualWeightPerAwb', label: 'Min Actual Weight per AWB', type: 'number', required: false, placeholder: '0' },
        { name: 'minVolumeWeightPerAwb', label: 'Min Volume Weight per AWB', type: 'number', required: false, placeholder: '0' },
        { name: 'maxActualWeightPerAwb', label: 'Max Actual Weight per AWB', type: 'number', required: false, placeholder: '0' },
        { name: 'maxVolumeWeightPerAwb', label: 'Max Volume Weight per AWB', type: 'number', required: false, placeholder: '0' },
        { name: 'minChargeableWeightPerAwb', label: 'Min Chargeable Weight per AWB', type: 'number', required: false, placeholder: '0' },
        { name: 'maxChargeableWeightPerAwb', label: 'Max Chargeable Weight per AWB', type: 'number', required: false, placeholder: '0' },
      ],
    },
    {
      title: 'Other Limits',
      description: 'Piece, shipment value, and dimension limits',
      fields: [
        { name: 'maxPcsPerAwb', label: 'Max Pcs per AWB', type: 'number', required: false, placeholder: '0' },
        { name: 'maxShipmentValue', label: 'Max Shipment Value', type: 'number', required: false, placeholder: '0' },
        { name: 'checkMaxShipmentValue', label: 'Check Max Shipment Value in AWB', type: 'checkbox', required: false },
        { name: 'maxDimsPerLineItem', label: 'Max Dims per Line Item', type: 'number', required: false, placeholder: '0' },
      ],
    },
    {
      title: 'References & Settings',
      description: 'Hub, GST, license location, and invoice settings',
      fields: [
        { name: 'hub', label: 'Hub', type: 'select', required: false, options: hubs.map((h: any) => ({ value: h._id, label: `${h.name} (${h.code})` })), placeholder: 'Select hub' },
        { name: 'gstNo', label: 'GST Number', type: 'text', required: false, placeholder: 'Enter GST number' },
        { name: 'serviceCode', label: 'Service Code (Additional)', type: 'text', required: false, placeholder: 'Enter service code' },
        { name: 'licenseLocation', label: 'License Location', type: 'select', required: false, options: licenseLocations.map((ll: any) => ({ value: ll._id, label: ll.name })), placeholder: 'Select license location' },
        { name: 'invoiceHead', label: 'Invoice Head', type: 'select', required: false, options: invoiceHeadOptions, placeholder: 'Select invoice head' },
        { name: 'authorityLetterHead', label: 'Authority Letter Head', type: 'text', required: false, placeholder: 'Enter authority letter head' },
      ],
    },
    {
      title: 'Tracking & URLs',
      description: 'Tracking configuration',
      fields: [
        { name: 'trackingUrl', label: 'Tracking URL', type: 'text', required: false, placeholder: 'https://...' },
        { name: 'trackingWebsite', label: 'Tracking Website', type: 'text', required: false, placeholder: 'https://...' },
      ],
    },
    {
      title: 'Additional Information',
      description: 'Other service details',
      fields: [
        { name: 'accountNumbers', label: 'Account Numbers', type: 'text', required: false, placeholder: 'Enter account numbers' },
        { name: 'receiverServiceCode', label: 'Receiver Service Code', type: 'text', required: false, placeholder: 'Enter receiver service code' },
        { name: 'currency', label: 'Currency', type: 'text', required: false, placeholder: 'Enter currency' },
        { name: 'csPerson', label: 'CS Person', type: 'text', required: false, placeholder: 'Enter CS person' },
        { name: 'billingPerson', label: 'Billing Person', type: 'text', required: false, placeholder: 'Enter billing person' },
        { name: 'cutOffTime', label: 'Cut Off Time', type: 'text', required: false, placeholder: 'Enter cut off time' },
        { name: 'supportedVendor', label: 'Supported Vendor', type: 'text', required: false, placeholder: 'Enter supported vendor' },
        { name: 'serviceLogo', label: 'Service Logo URL', type: 'text', required: false, placeholder: 'Enter logo URL' },
        { name: 'returnAddress', label: 'Return Address', type: 'textarea', required: false, placeholder: 'Enter return address' },
        { name: 'notes', label: 'Notes', type: 'textarea', required: false, placeholder: 'Enter notes' },
      ],
    },
  ];

  const handleCreate = () => {
    setEditingService(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (service: Service) => {
    setEditingService(transformServiceForEdit(service) as Service);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = (service: Service) => {
    deleteServiceMutation.mutate(service._id);
  };

  const handleSubmit = (data: any) => {
    // Transform data - only include code if provided
    const transformedData = { ...data };
    if (transformedData.code) {
      transformedData.code = transformedData.code.toUpperCase();
    } else {
      delete transformedData.code; // Let backend auto-generate
    }
    
    if (editingService) {
      updateServiceMutation.mutate({ id: editingService._id, data: transformedData });
    } else {
      createServiceMutation.mutate(transformedData);
    }
  };

  const handleRefresh = () => {
    refetch();
    toast({ title: 'Refreshed', description: 'Service data has been refreshed' });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') params.append(key, value);
      });
      const response = await axios.get(`/api/master-data/service/export?${params.toString()}`);
      if (response.data.success) {
        const data = response.data.data;
        if (!data || data.length === 0) {
          toast({
            title: 'No data to export',
            description: 'No services match the current filters.',
            variant: 'destructive',
          });
          return;
        }
        const exportData = data.map((s: Service) => ({
          Code: s.code,
          Name: s.name,
          Type: s.type,
          Status: s.status,
          'Created At': new Date(s.createdAt).toLocaleString(),
        }));
        const headers = Object.keys(exportData[0] || {});
        const rows = exportData.map((row: any) => headers.map((h) => row[h] || ''));
        const csvContent = buildCsv(headers, rows, { includeBom: true, quoteStrings: true });
        downloadText(
          csvContent,
          `services-${new Date().toISOString().split('T')[0]}.csv`,
          'text/csv;charset=utf-8;'
        );
        toast({ title: 'Success', description: `Exported ${data.length} services` });
      }
    } catch (error: any) {
      toast({ title: 'Export Failed', description: error.response?.data?.error?.message || 'Failed to export', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Service Management</h1>
          <p className="text-muted-foreground">Manage services and their configurations</p>
        </div>
      </div>

      <MasterDataTable<Service>
        title="Services"
        description="Manage and configure service information"
        data={services}
        columns={columns}
        isLoading={isLoading}
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search services..."
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={filters}
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        deleteConfirm
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingService(null); setServerErrors({}); }}
        title={editingService ? 'Edit Service' : 'Create Service'}
        sections={formSections}
        initialData={editingService}
        onSubmit={handleSubmit}
        isLoading={createServiceMutation.isPending || updateServiceMutation.isPending}
        mode={editingService ? 'edit' : 'create'}
        serverErrors={serverErrors}
      />
    </div>
  );
};

export default ServiceManagement;
